import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Button,
  Select,
  DatePicker,
  TimePicker,
  message,
  Col,
  Row,
  Spin,
  Input,
} from "antd";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  editScheduleSchema,
  bulkScheduleSchema,
  EditScheduleFormData,
  BulkScheduleFormData,
} from "../../validations/scheduleSchema";
import {
  useCreateSchedule,
  useUpdateSchedule,
  Schedule,
  useStaff,
  BulkSchedulePayload,
} from "../../hooks/useSchedule";
import { useLocations } from "../../hooks/useUsers";
import { useAllTables } from "../../hooks/useTables";
import dayjs from "dayjs";
import { PERMISSIONS, usePermissions } from "../../context/PermissionContext";
import { CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";

const { Option } = Select;

interface ScheduleFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  schedule?: Schedule;
  locationFilter?: number;
}

const ScheduleFormModal: React.FC<ScheduleFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  schedule,
  locationFilter,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<
    number | undefined
  >(locationFilter);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { hasPermission } = usePermissions();
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Determine editing state when schedule changes
  useEffect(() => {
    setIsEditing(!!schedule);
  }, [schedule]);

  // Set up forms
  const editForm = useForm<EditScheduleFormData>({
    resolver: zodResolver(editScheduleSchema),
    defaultValues: {
      table_id: undefined,
    },
  });

  const bulkForm = useForm<BulkScheduleFormData>({
    resolver: zodResolver(bulkScheduleSchema),
    defaultValues: {
      user_id: [],
      table_id: [],
      start_date: dayjs().format("YYYY-MM-DD"),
      end_date: dayjs().format("YYYY-MM-DD"),
      start_time: "",
      end_time: "",
      shift_type: "morning",
      status: "scheduled",
      notes: "",
    },
  });

  // Use the appropriate form based on editing status
  const { control, handleSubmit, formState } = isEditing ? editForm : bulkForm;
  const errors = formState.errors as any;

  // API hooks
  const { mutate: createSchedule, isPending: isCreating } = useCreateSchedule();
  const { mutate: updateSchedule, isPending: isUpdating } = useUpdateSchedule();

  // Fetch staff users (user_type_id = 2)
  const { data: staffData, isLoading: isLoadingStaff } = useStaff();

  // Fetch locations using the existing hook
  const { data: locationsData, isLoading: isLoadingLocations } = useLocations();

  // Fetch ALL tables with filtering (no pagination)
  const { data: tablesData, isLoading: isLoadingTables } = useAllTables({
    location_id: selectedLocationId || "",
  });

  // Reset form when modal is opened/closed or when schedule changes
  useEffect(() => {
    if (visible) {
      if (isEditing && schedule) {
        console.log("Editing schedule:", schedule);
        editForm.reset({
          user_id: schedule.user_id,
          table_id: schedule.table_id ?? undefined,
          schedule_date: schedule.schedule_date,
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          shift_type: schedule.shift_type as "morning" | "day" | "night",
          status: schedule.status as "scheduled" | "completed" | "canceled",
          notes: schedule.notes || "",
        });

        if (schedule.user?.location_id) {
          setSelectedLocationId(schedule.user.location_id);
        }
      } else {
        // Reset bulk form for creating
        bulkForm.reset({
          user_id: [],
          table_id: [],
          start_date: dayjs().format("YYYY-MM-DD"),
          end_date: dayjs().format("YYYY-MM-DD"),
          start_time: "",
          end_time: "",
          shift_type: "morning",
          status: "scheduled",
          notes: "",
        });
        setSelectedLocationId(locationFilter);
      }
    }
  }, [visible, schedule, editForm, bulkForm, isEditing, locationFilter]);

  const onSubmitEditForm: SubmitHandler<EditScheduleFormData> = (data) => {
    if (schedule) {
      setIsSubmitting(true);
      updateSchedule(
        {
          id: schedule.id,
          payload: data,
        },
        {
          onSuccess: () => {
            message.success("Schedule updated successfully!");
            editForm.reset();
            onSubmit();
            onCancel();
            setIsSubmitting(false);
          },
          onError: (error) => {
            console.error("Update error:", error);
            message.error("Failed to update schedule. Please try again.");
            setIsSubmitting(false);
          },
        }
      );
    }
  };

  const onSubmitBulkForm: SubmitHandler<BulkScheduleFormData> = (data) => {
    setIsSubmitting(true);

    // Validate required fields
    if (!data.user_id.length) {
      message.error("Please select at least one staff member");
      setIsSubmitting(false);
      return;
    }
    if (!data.table_id.length) {
      message.error("Please select at least one table");
      setIsSubmitting(false);
      return;
    }

    // Convert form data to API payload
    const payload: BulkSchedulePayload = {
      user_id: data.user_id,
      table_id: data.table_id,
      start_date: data.start_date,
      end_date: data.end_date,
      start_time: data.start_time,
      end_time: data.end_time,
      shift_type: data.shift_type,
      status: data.status,
      notes: data.notes || "",
    };

    createSchedule(payload, {
      onSuccess: () => {
        message.success("Schedules created successfully!");
        bulkForm.reset({
          user_id: [],
          table_id: [],
          start_date: dayjs().format("YYYY-MM-DD"),
          end_date: dayjs().format("YYYY-MM-DD"),
          start_time: "",
          end_time: "",
          shift_type: "morning",
          status: "scheduled",
          notes: "",
        });
        onSubmit();
        onCancel();
        setIsSubmitting(false);
      },
      onError: (error) => {
        console.error("Create error:", error);
        message.error("Failed to create schedules. Please try again.");
        setIsSubmitting(false);
      },
    });
  };

  const renderDateSelector = () => {
    return (
      <Controller
        name="schedule_date"
        control={control as any}
        render={({ field }) => (
          <Form.Item
            label="Date"
            validateStatus={errors?.schedule_date ? "error" : ""}
            help={errors?.schedule_date?.message as React.ReactNode}
            required
          >
            <DatePicker
              style={{ width: "100%" }}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date) =>
                field.onChange(date ? date.format("YYYY-MM-DD") : "")
              }
              format="YYYY-MM-DD"
              placeholder="Select date"
              suffixIcon={<CalendarOutlined />}
            />
          </Form.Item>
        )}
      />
    );
  };

  const renderStaffSelector = () => {
    if (isEditing) {
      return (
        <Controller
          name="user_id"
          control={control as any}
          render={({ field }) => (
            <Form.Item
              label="Staff Member"
              validateStatus={errors?.user_id ? "error" : ""}
              help={errors?.user_id?.message as React.ReactNode}
              required
            >
              <Select
                {...field}
                placeholder="Select staff member"
                loading={isLoadingStaff}
                showSearch
                optionFilterProp="children"
              >
                {staffData?.data?.map((staff: any) => (
                  <Option key={staff.id} value={staff.id}>
                    {staff.name} ({staff.location?.name})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        />
      );
    }

    return (
      <Controller
        name="user_id"
        control={control as any}
        render={({ field }) => (
          <Form.Item
            label="Staff Members"
            validateStatus={errors?.user_id ? "error" : ""}
            help={errors?.user_id?.message as React.ReactNode}
            required
          >
            <Select
              {...field}
              mode="multiple"
              placeholder="Select staff members"
              loading={isLoadingStaff}
              showSearch
              optionFilterProp="children"
            >
              {staffData?.data?.map((staff: any) => (
                <Option key={staff.id} value={staff.id}>
                  {staff.name} ({staff.location?.name})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      />
    );
  };

  const renderTableSelector = () => {
    if (isEditing) {
      return (
        <Controller
          name="table_id"
          control={control as any}
          render={({ field }) => (
            <Form.Item
              label="Table"
              validateStatus={errors?.table_id ? "error" : ""}
              help={errors?.table_id?.message as React.ReactNode}
              required
            >
              <Select
                {...field}
                placeholder="Select a table"
                showSearch
                loading={isLoadingTables}
                filterOption={(input, option) => {
                  if (!option || !input) return false;
                  const optionText = String(option.children || "");
                  return optionText.toLowerCase().includes(input.toLowerCase());
                }}
                notFoundContent={
                  isLoadingTables ? (
                    <span>Loading...</span>
                  ) : selectedLocationId ? (
                    <span>No tables found for selected location</span>
                  ) : (
                    <span>Select a location to view tables</span>
                  )
                }
              >
                {tablesData?.map((table: any) => (
                  <Option key={table.id} value={table.id}>
                    Table #{table.number} - {table.location.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        />
      );
    }

    return (
      <Controller
        name="table_id"
        control={control as any}
        render={({ field }) => (
          <Form.Item
            label="Tables"
            validateStatus={errors?.table_id ? "error" : ""}
            help={errors?.table_id?.message as React.ReactNode}
            required
          >
            <Select
              {...field}
              mode="multiple"
              placeholder="Select tables"
              showSearch
              loading={isLoadingTables}
              filterOption={(input, option) => {
                if (!option || !input) return false;
                const optionText = String(option.children || "");
                return optionText.toLowerCase().includes(input.toLowerCase());
              }}
              notFoundContent={
                isLoadingTables ? (
                  <span>Loading...</span>
                ) : selectedLocationId ? (
                  <span>No tables found for selected location</span>
                ) : (
                  <span>Select a location to view tables</span>
                )
              }
            >
              {tablesData?.map((table: any) => (
                <Option key={table.id} value={table.id}>
                  Table #{table.number} - {table.location.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      />
    );
  };

  return (
    <Modal
      title={isEditing ? "Edit Schedule" : "Create Schedules"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      maskClosable={false}
      destroyOnClose={true}
    >
      <Spin spinning={isSubmitting}>
        <Form
          layout="vertical"
          onFinish={
            isEditing
              ? handleSubmit(onSubmitEditForm as any)
              : handleSubmit(onSubmitBulkForm as any)
          }
        >
          {/* Staff Selection */}
          {renderStaffSelector()}

          {/* Location Filter for Tables */}
          <Form.Item label="Filter Tables by Location">
            <Select
              placeholder="Select location to filter tables"
              allowClear
              loading={isLoadingLocations}
              onChange={(value) => setSelectedLocationId(value)}
              value={selectedLocationId}
              disabled={!canSeeAllLocations}
            >
              {locationsData?.map((location: any) => (
                <Option key={location.id} value={location.id}>
                  {location.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Table Selection */}
          {renderTableSelector()}

          {/* Date Selection */}
          {isEditing ? (
            <Row gutter={16}>
              <Col xs={24}>{renderDateSelector()}</Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Controller
                  name="start_date"
                  control={control as any}
                  render={({ field }) => (
                    <Form.Item
                      label="Start Date"
                      validateStatus={errors?.start_date ? "error" : ""}
                      help={errors?.start_date?.message as React.ReactNode}
                      required
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date ? date.format("YYYY-MM-DD") : "")
                        }
                        format="YYYY-MM-DD"
                        disabledDate={(current) =>
                          current && current < dayjs().startOf("day")
                        }
                        placeholder="Select start date"
                        suffixIcon={<CalendarOutlined />}
                      />
                    </Form.Item>
                  )}
                />
              </Col>
              <Col xs={24} md={12}>
                <Controller
                  name="end_date"
                  control={control as any}
                  render={({ field }) => (
                    <Form.Item
                      label="End Date"
                      validateStatus={errors?.end_date ? "error" : ""}
                      help={errors?.end_date?.message as React.ReactNode}
                      required
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date ? date.format("YYYY-MM-DD") : "")
                        }
                        format="YYYY-MM-DD"
                        disabledDate={(current) =>
                          current && current < dayjs().startOf("day")
                        }
                        placeholder="Select end date"
                        suffixIcon={<CalendarOutlined />}
                      />
                    </Form.Item>
                  )}
                />
              </Col>
            </Row>
          )}

          {/* Shift Type */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="shift_type"
                control={control as any}
                render={({ field }) => (
                  <Form.Item
                    label="Shift Type"
                    validateStatus={errors?.shift_type ? "error" : ""}
                    help={errors?.shift_type?.message as React.ReactNode}
                    required
                  >
                    <Select {...field} placeholder="Select shift type">
                      <Option value="morning">Morning</Option>
                      <Option value="day">Day</Option>
                      <Option value="night">Night</Option>
                    </Select>
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="status"
                control={control as any}
                render={({ field }) => (
                  <Form.Item
                    label="Status"
                    validateStatus={errors?.status ? "error" : ""}
                    help={errors?.status?.message as React.ReactNode}
                    required
                  >
                    <Select {...field} placeholder="Select status">
                      <Option value="scheduled">Scheduled</Option>
                      <Option value="completed">Completed</Option>
                      <Option value="canceled">Canceled</Option>
                    </Select>
                  </Form.Item>
                )}
              />
            </Col>
          </Row>

          {/* Time Range */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="start_time"
                control={control as any}
                render={({ field }) => (
                  <Form.Item
                    label="Start Time"
                    validateStatus={errors?.start_time ? "error" : ""}
                    help={errors?.start_time?.message as React.ReactNode}
                    required
                  >
                    <TimePicker
                      style={{ width: "100%" }}
                      format="h:mm A"
                      value={
                        field.value ? dayjs(field.value, "HH:mm:ss") : null
                      }
                      onChange={(time) =>
                        field.onChange(time ? time.format("HH:mm:ss") : "")
                      }
                      use12Hours={true}
                      showNow={false}
                      suffixIcon={<ClockCircleOutlined />}
                      placeholder="Select start time"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="end_time"
                control={control as any}
                render={({ field }) => (
                  <Form.Item
                    label="End Time"
                    validateStatus={errors?.end_time ? "error" : ""}
                    help={errors?.end_time?.message as React.ReactNode}
                    required
                  >
                    <TimePicker
                      style={{ width: "100%" }}
                      format="h:mm A"
                      value={
                        field.value ? dayjs(field.value, "HH:mm:ss") : null
                      }
                      onChange={(time) =>
                        field.onChange(time ? time.format("HH:mm:ss") : "")
                      }
                      use12Hours={true}
                      showNow={false}
                      suffixIcon={<ClockCircleOutlined />}
                      placeholder="Select end time"
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>

          {/* Notes */}
          <Controller
            name="notes"
            control={control as any}
            render={({ field }) => (
              <Form.Item
                label="Notes"
                validateStatus={errors?.notes ? "error" : ""}
                help={errors?.notes?.message as React.ReactNode}
              >
                <Input.TextArea
                  {...field}
                  rows={4}
                  placeholder="Enter any notes or comments"
                  value={field.value || ""}
                />
              </Form.Item>
            )}
          />

          {/* Form Actions */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isCreating || isUpdating || isSubmitting}
              disabled={isSubmitting}
            >
              {isEditing ? "Update Schedule" : "Create Schedules"}
            </Button>
            <Button
              onClick={onCancel}
              style={{ marginLeft: 8 }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ScheduleFormModal;
